import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import apiClient from "@/lib/apiClient";
import { LayoutDashboard, FolderKanban, LogOut, Ship, Users, Menu, X, Shield, CreditCard, LifeBuoy, BookOpen, Sparkles } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
  { to: "/docs", label: "Help & Docs", icon: BookOpen, testid: "nav-docs" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState(null);

  useEffect(() => {
    apiClient.get("/org").then(({ data }) => setOrg(data.org)).catch(() => {});
  }, []);
  const canUpgrade = user?.role === "owner" || user?.role === "admin";

  // Dogfood Tugboat: load our own feedback widget ONLY inside the logged-in admin panel
  // (not on the public marketing site). Cleaned up when leaving the authenticated layout.
  useEffect(() => {
    if (document.getElementById("tugboat-widget-script")) return;
    const s = document.createElement("script");
    s.id = "tugboat-widget-script";
    s.src = "https://tugboatapp.com/widget.js";
    s.setAttribute("data-project-key", "pk_7b39f44cc42d4e78b36cd59554fc5792");
    s.async = true;
    document.body.appendChild(s);
    return () => {
      document.getElementById("tugboat-widget-script")?.remove();
      document.getElementById("tugboat-widget-root")?.remove();
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-16 bg-sunny border-b-2 border-ink flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-orange border-2 border-ink rounded-xl flex items-center justify-center shadow-nb-sm">
            <Ship className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-700 text-xl tracking-tight">Tugboat</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="h-10 w-10 flex items-center justify-center bg-white border-2 border-ink rounded-xl shadow-nb-sm">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Backdrop */}
      {open && <div className="lg:hidden fixed inset-0 bg-ink/40 z-40" onClick={() => setOpen(false)} />}

      {/* Sidebar / drawer */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-sunny border-r-2 border-ink flex flex-col z-50 transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-20 flex items-center justify-between gap-2.5 px-6">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 bg-orange border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm">
              <Ship className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-700 text-2xl tracking-tight">Tugboat</span>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden h-8 w-8 flex items-center justify-center bg-white border-2 border-ink rounded-lg">
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all border-2 ${
                  isActive ? "bg-white border-ink shadow-nb-sm" : "border-transparent text-ink/70 hover:bg-white/60 hover:text-ink"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
          {(user?.role === "owner" || user?.role === "admin") && (
            <NavLink
              to="/billing"
              data-testid="nav-billing"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all border-2 ${
                  isActive ? "bg-white border-ink shadow-nb-sm" : "border-transparent text-ink/70 hover:bg-white/60 hover:text-ink"
                }`
              }
            >
              <CreditCard className="h-5 w-5" />
              Billing
            </NavLink>
          )}
          {user?.role === "admin" && user?.is_staff && (
            <NavLink
              to="/platform"
              data-testid="nav-platform"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all border-2 ${
                  isActive ? "bg-ink text-cream border-ink shadow-nb-sm" : "border-transparent text-ink/70 hover:bg-ink hover:text-cream"
                }`
              }
            >
              <Shield className="h-5 w-5" />
              Mission Control
            </NavLink>
          )}
        </nav>
        <div className="p-4">
          <div className="bg-white border-2 border-ink rounded-2xl p-3 shadow-nb-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 w-9 rounded-full bg-teal border-2 border-ink flex items-center justify-center text-sm font-800 uppercase">
                {(user?.name || user?.email || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-700 truncate" data-testid="current-user-name">{user?.name || "Developer"}</p>
                <p className="text-xs text-ink/60 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-bold border-2 border-ink bg-cream hover:bg-danger hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {org?.on_trial && (
          <div data-testid="trial-banner" className={`border-b-2 border-ink px-4 sm:px-6 py-2.5 flex items-center justify-center gap-3 text-center text-xs sm:text-sm font-700 ${org.trial_days_left <= 14 ? "bg-danger text-white" : "bg-sunny text-ink"}`}>
            <Sparkles className="h-4 w-4 shrink-0" />
            <span data-testid="trial-banner-text">
              {org.trial_days_left <= 14
                ? `Your Business trial ends in ${org.trial_days_left} day${org.trial_days_left === 1 ? "" : "s"} — upgrade to keep white-label, session replay & long recordings.`
                : `Business trial active · ${org.trial_days_left} days left of full white-label, session replay & long recordings.`}
            </span>
            {canUpgrade && (
              <NavLink to="/billing" data-testid="trial-upgrade-cta"
                className={`shrink-0 px-3 py-1 rounded-full border-2 border-ink font-800 text-xs transition-colors ${org.trial_days_left <= 14 ? "bg-white text-ink hover:bg-cream" : "bg-orange text-white hover:bg-ink"}`}>
                Upgrade →
              </NavLink>
            )}
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
